import React from 'react';

import { ArrowLeft, ArrowRight, type LucideProps } from 'lucide-react';

import NavLogo from 'common/NavLogo';
import { cn } from 'common/style/utils';
import ButtonV2 from 'common/ui/ButtonV2';
import Flex from 'common/ui/Flex';
import VStack from 'common/ui/VStack';

type RegistrationWrapperProps = React.ComponentProps<typeof Flex> & {
  action?: React.ReactNode;
};

const RegistrationWrapper = ({
  action,
  children,
  className,
  ...props
}: RegistrationWrapperProps) => {
  return (
    <VStack
      className={cn(
        'bg-background',
        'p-4 sm:pb-12 box-border ',
        'items-center justify-start',
        'sm:justify-center',
        'min-h-screen',
        className,
      )}
      {...props}>
      <VStack data-slot="wrapper" className="w-full gap-10">
        <Flex className="w-full items-center justify-between">
          <NavLogo />
          {action}
        </Flex>
        {children}
      </VStack>
    </VStack>
  );
};

type RegistrationHeaderProps = React.ComponentProps<typeof VStack>;

const RegistrationHeader = ({ children, className, ...props }: RegistrationHeaderProps) => {
  return (
    <VStack data-slot="header" className={cn('gap-2', className)} {...props}>
      {children}
    </VStack>
  );
};

type RegistrationTitleProps = React.ComponentProps<'h1'>;

const RegistrationTitle = ({ children, className, ...props }: RegistrationTitleProps) => {
  return (
    <h1 data-slot="title" className={cn('text-4xl font-semibold', className)} {...props}>
      {children}
    </h1>
  );
};

type RegistrationDescriptionProps = React.ComponentProps<'p'>;

const RegistrationDescription = ({
  children,
  className,
  ...props
}: RegistrationDescriptionProps) => {
  return (
    <p data-slot="description" className={cn('text-lg text-gray-110', className)} {...props}>
      {children}
    </p>
  );
};

type RegistrationContentProps = React.ComponentProps<typeof VStack>;

const RegistrationContent = ({ children, className, ...props }: RegistrationContentProps) => {
  return (
    <VStack data-slot="content" className={cn('gap-10', className)} {...props}>
      {children}
    </VStack>
  );
};

type NavigationButtonConfig = {
  disabled?: boolean;
  href?: string;
  onClick?: () => void;
  visible?: boolean;
  text?: string;
  endIcon?: React.ComponentType<LucideProps>;
};

type RegistrationNavigationProps = React.ComponentProps<typeof Flex> & {
  back?: NavigationButtonConfig;
  continue?: NavigationButtonConfig;
  skip?: NavigationButtonConfig;
  className?: string;
  isLoading?: boolean;
};

const defaultBack: NavigationButtonConfig = {
  disabled: false,
  href: undefined,
  onClick: undefined,
  visible: true,
  text: 'Back',
};

const defaultContinue: NavigationButtonConfig = {
  disabled: false,
  href: undefined,
  onClick: undefined,
  visible: true,
  text: 'Continue',
};

const defaultSkip: NavigationButtonConfig = {
  disabled: false,
  href: undefined,
  onClick: undefined,
  visible: false,
  text: 'Skip',
};

const RegistrationNavigation = ({
  back: backProps,
  continue: continueProps,
  skip: skipProps,
  className,
  isLoading = false,
  ...props
}: RegistrationNavigationProps) => {
  const back = { ...defaultBack, ...backProps };
  const continueButton = { ...defaultContinue, ...continueProps };
  const skip = { ...defaultSkip, ...skipProps };

  return (
    <Flex
      data-slot="navigation"
      className={cn('items-center justify-between', className)}
      {...props}>
      {back.visible ? (
        <NavigationButton
          {...back}
          startIcon={ArrowLeft}
          variant="outlined"
          disabled={back.disabled || isLoading}>
          {back.text}
        </NavigationButton>
      ) : (
        <div />
      )}
      <Flex className="gap-6">
        {skip.visible && (
          <NavigationButton {...skip} variant="plain" disabled={skip.disabled || isLoading}>
            {skip.text}
          </NavigationButton>
        )}
        <NavigationButton
          endIcon={ArrowRight}
          {...continueButton}
          loading={isLoading}
          type="submit">
          {continueButton.text}
        </NavigationButton>
      </Flex>
    </Flex>
  );
};

type NavigationButtonProps = React.ComponentProps<typeof ButtonV2> & {
  href?: string;
};

const NavigationButton = ({ children, href, ...props }: NavigationButtonProps) => {
  const button = (
    <ButtonV2 size="large" {...props}>
      {children}
    </ButtonV2>
  );

  if (href) {
    return <a href={href}>{button}</a>;
  }

  return button;
};

RegistrationWrapper.Header = RegistrationHeader;
RegistrationWrapper.Title = RegistrationTitle;
RegistrationWrapper.Description = RegistrationDescription;
RegistrationWrapper.Content = RegistrationContent;
RegistrationWrapper.Navigation = RegistrationNavigation;

export default RegistrationWrapper;
